import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";

import { environment } from "@environments/environment";
import { Asset } from "@app/_models/_DTOs/asset";
import { DataService } from "@app/_services/data.service";
import { AssetHistoryActionType } from "@app/_models/assetHistoryActionType";
import { AssetHistory } from "@app/_models/_DTOs/assetHistory";
import { AssetType } from "@app/_models/enums/assetType";
import { map } from "rxjs/operators";
import { InventoryFoundAssetService } from "./inventoryFoundAsset.service";
import { AssetViewModel } from "@app/_models/_viewModels/asset.viewmodel";
import { ITuple } from "@app/_models/tuple";
import { AssetDiscardProposal } from "@app/_models/_DTOs/assetDiscardProposal";
import { DiscardActionViewModel } from "@app/_models/_viewModels/discardAction.viewmodel";
import { MultiDiscardActionViewModel } from "@app/_models/_viewModels/multiAssetDiscard.viewmodel";
import { AssetDiscardResponse } from "@app/_models/_response/assetDiscard.response";
import { AssetHelper } from "@app/_helpers/asset.helper";
import { NumberHelper } from "@app/_helpers/number.helper";

@Injectable({ providedIn: "root" })
export class AssetService extends DataService<Asset> {
  constructor(
    http: HttpClient,
    private inventoryFoundAssetService: InventoryFoundAssetService
  ) {
    super(http, "api/assets");
  }

  getAssetsForDiscarding(
    page: number,
    pageSize: number,
    sorts: string = "",
    filters: string = ""
  ) {
    var params = new HttpParams()
      .append("sorts", `${sorts}`)
      .append("filters", `${filters}`)
      .append("page", `${page}`)
      .append("pageSize", `${pageSize}`);
    return this.http.get<ITuple<AssetDiscardProposal>>(
      `${environment.apiUrl}/${this.endpoint}/ForDiscarding`,
      { params }
    );
  }

  getAssetsForDiscardingExcel(
    sorts: string = "",
    filters: string = ""
  ) {
    var params = new HttpParams()
      .append("sorts", `${sorts}`)
      .append("filters", `${filters}`);
    return this.http.get(`${environment.apiUrl}/${this.endpoint}/ForDiscarding/Excel`, { params, responseType: "blob" });
  }

  getNextInventoryCodeNumber(legalEntityId: number, assetType: AssetType) {
    return this.http.get<number>(
      `${environment.apiUrl}/${this.endpoint}/GetNextInventoryCodeNumber/${legalEntityId}/${assetType}`
    );
  }

  getAssetsWithConflictingInventoryCode(
    asset: Asset,
    legalEntityId: number,
    assetType: AssetType
  ) {
    return this.getPage(
      1,
      5,
      "-Created",
      "InventoryCodeNumber==" +
      asset.inventoryCodeNumber +
      ",Id!=" +
      asset.id +
      ",Department.LegalEntityId==" +
      legalEntityId +
      ",Type==" +
      assetType
    ).pipe(map((x) => x.item1));
  }

  getAssetsWithConflictingInventoryCodeById(
    assetId: number,
    legalEntityId: number,
    assetType: AssetType,
    inventoryCodeNumber: number
  ) {
    return this.getPage(
      1,
      5,
      "-Created",
      "InventoryCodeNumber==" +
      inventoryCodeNumber +
      ",Id!=" +
      assetId +
      ",Department.LegalEntityId==" +
      legalEntityId +
      ",Type==" +
      assetType
    ).pipe(map((x) => x.item1));
  }

  getAssetsByBarcode(barcode: string, assetId: number) {
    let service = this;
    return this.getPage(
      1,
      5,
      "-Created",
      "BarCode==" + barcode + ",Id!=" + assetId
    ).pipe(
      map((assetsWithSameBarcode) => {
        let names: string[] = [];
        return service.inventoryFoundAssetService
          .getPage(1, 5, "-Created", "BarCode==" + barcode)
          .pipe(
            map((foundAssetsResult) => {
              let conflictingAssetsByBarCode = assetsWithSameBarcode.item1;
              let conflictingFoundAssets = foundAssetsResult.item1;

              names.push(...conflictingAssetsByBarCode.map((x) => x.name));
              names.push(...conflictingFoundAssets.map((x) => x.name));
              return names;
            })
          );
      })
    );
  }

  getFoundAssetsByBarcode(barcode: string, inventoryFoundAssetId: number) {
    let service = this;
    return service.inventoryFoundAssetService
      .getPage(
        1,
        5,
        "-Created",
        "BarCode==" + barcode + ",Id!=" + inventoryFoundAssetId
      )
      .pipe(
        map((foundAssetsResult) => {
          let names: string[] = [];
          return service.getPage(1, 5, "-Created", "BarCode==" + barcode).pipe(
            map((assetsWithSameBarcode) => {
              let conflictingAssetsByBarCode = assetsWithSameBarcode.item1;
              let conflictingFoundAssets = foundAssetsResult.item1;

              names.push(...conflictingAssetsByBarCode.map((x) => x.name));
              names.push(...conflictingFoundAssets.map((x) => x.name));
              return names;
            })
          );
        })
      );
  }

  getAssignmentAction(asset: Asset, type: AssetHistoryActionType = null) {
    var assetHistory = asset.assetHistory;
    if (type) {
      assetHistory = asset.assetHistory.filter((x) => x.actionType == type);
    } else {
      assetHistory = asset.assetHistory.filter(
        (x) => x.actionType == AssetHistoryActionType.AssignToEmployee
      );
      if (assetHistory.length == 0) {
        assetHistory = asset.assetHistory.filter(
          (x) => x.actionType == AssetHistoryActionType.AssignToLocation
        );
      }
    }
    if (assetHistory.length > 0) {
      return assetHistory.sort((val1, val2) => {
        return (
          new Date(val2.logTime).getTime() - new Date(val1.logTime).getTime()
        );
      })[0];
    }
    return null;
  }

  getMostRecentDateOfAction(
    assetHistory: AssetHistory[],
    type: AssetHistoryActionType
  ) {
    var mostRecentDate: Date = null;
    assetHistory.forEach((action) => {
      if (action.actionType == type) {
        var logTime = new Date(action.logTime);
        if (mostRecentDate) {
          if (logTime.getTime() > mostRecentDate.getTime()) {
            mostRecentDate = logTime;
          }
        } else {
          mostRecentDate = logTime;
        }
      }
    });
    return mostRecentDate;
  }

  assetActionError(asset: Asset, assetAction: AssetHistory) {
    if (assetAction.actionType == AssetHistoryActionType.UnassignFromEmployee) {
      if (assetAction.documents.length == 0) {
        return true;
      }
      return false;
    } else if (
      assetAction.actionType == AssetHistoryActionType.UnassignFromLocation
    ) {
      if (assetAction.oldLocation.personInCharge) {
        if (assetAction.documents.length == 0) {
          return true;
        }
      }
      return false;
    }
  }

  assetUnassignmentError(asset: Asset) {
    if (
      asset.assetHistory.some((assetAction) =>
        this.assetActionError(asset, assetAction)
      )
    ) {
      // assignment not confirmed
      return false;
    } else {
      // assignment confirmed
      return true;
    }
  }

  assetActionNeedsConfirmation(asset: Asset, assetAction: AssetHistory) {
    if (assetAction.actionType == AssetHistoryActionType.AssignToEmployee) {
      var mostRecentDate = this.getMostRecentDateOfAction(
        asset.assetHistory,
        assetAction.actionType
      );
      var logTime = new Date(assetAction.logTime);
      // želimo samo najnoviju akciju tipa AssignToEmployee
      if (mostRecentDate) {
        if (logTime.getTime() == mostRecentDate.getTime()) {
          if (asset.employee) {
            // gledamo samo akcije koje još uvijek vrijede
            if (asset.employee.id == assetAction.newEmployee.id) {
              if (assetAction.documents.length == 0) {
                return true;
              }
            }
          }
        }
      }
      return false;
    } else if (
      assetAction.actionType == AssetHistoryActionType.AssignToLocation
    ) {
      var mostRecentDate = this.getMostRecentDateOfAction(
        asset.assetHistory,
        assetAction.actionType
      );
      var logTime = new Date(assetAction.logTime);
      // želimo samo najnoviju akciju tipa AssignToEmployee
      if (mostRecentDate) {
        if (logTime.getTime() == mostRecentDate.getTime()) {
          if (asset.location) {
            // gledamo samo akcije koje još uvijek vrijede
            if (asset.location.id == assetAction.newLocation.id) {
              if (asset.location.personInCharge) {
                if (assetAction.documents.length == 0) {
                  return true;
                }
              }
            }
          }
        }
      }
      return false;
    }
  }

  isAssetAssignmentConfirmed(asset: Asset) {
    if (
      asset.assetHistory.some((assetAction) =>
        this.assetActionNeedsConfirmation(asset, assetAction)
      )
    ) {
      // assignment not confirmed
      return false;
    } else {
      // assignment confirmed
      return true;
    }
  }

  getAssetDiscardProposal(assetId: number) {
    return this.http.get<AssetDiscardProposal>(
      `${environment.apiUrl}/${this.endpoint}/GetAssetDiscardProposal/${assetId}`
    );
  }

  discardAsset(assetDiscardViewModel: DiscardActionViewModel) {
    return this.http.post(
      `${environment.apiUrl}/${this.endpoint}/DiscardAsset`,
      assetDiscardViewModel,
      {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
        }),
        responseType: "blob",
      }
    );
  }

  discardAssets(multiAssetDiscardViewModel: MultiDiscardActionViewModel) {
    return this.http.post(
      `${environment.apiUrl}/${this.endpoint}/DiscardAssets`,
      multiAssetDiscardViewModel,
      {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
        }),
        responseType: "blob",
      }
    );
  }

  getAssetViewModel(asset?: Asset): AssetViewModel {
    if (asset) {
      let assetViewModel = {
        id: asset.id ? asset.id : -1,
        type: asset.type ? asset.type : undefined,
        inventoryCode: asset.inventoryCode
          ? NumberHelper.pad(
            parseInt(AssetHelper.ParseInventoryCodeNumber(asset)),
            4
          )
          : "",
        inventoryCodeNumber: asset.inventoryCodeNumber
          ? asset.inventoryCodeNumber
          : 0,
        barCode: asset.barCode ? asset.barCode : "",
        name: asset.name ? asset.name : "",
        description: asset.description ? asset.description : "",
        serialNumber: asset.serialNumber ? asset.serialNumber : "",
        qty: asset.qty ? asset.qty : 1,
        purchasePrice: asset.purchasePrice ? asset.purchasePrice : 0,
        currentValue: asset.currentValue ? asset.currentValue : undefined,
        purchaseDate: asset.purchaseDate ? asset.purchaseDate : new Date(),
        notes: asset.notes ? asset.notes : "",
        condition: asset.condition ? asset.condition : undefined,
        retiredDate: asset.retiredDate ? asset.retiredDate : undefined,
        warrantyExpirationDate: asset.warrantyExpirationDate
          ? asset.warrantyExpirationDate
          : undefined,
        manufacturer: asset.manufacturer ? asset.manufacturer : "",
        media: asset.media ? asset.media : undefined,
        departmentId: asset.department ? asset.department.id : undefined,
        employeeId: asset.employee ? asset.employee.id : undefined,
        assetGroupId: asset.assetGroup ? asset.assetGroup.id : undefined,
        locationId: asset.location ? asset.location.id : undefined,
        supplierId: asset.supplier ? asset.supplier.id : undefined,
      };
      return assetViewModel;
    } else {
      let assetViewModel = {
        id: -1,
        type: undefined,
        inventoryCode: "",
        inventoryCodeNumber: 0,
        barCode: "",
        name: "",
        description: "",
        serialNumber: "",
        qty: 1,
        purchasePrice: 0,
        currentValue: undefined,
        purchaseDate: new Date(),
        notes: "",
        condition: undefined,
        retiredDate: undefined,
        warrantyExpirationDate: undefined,
        manufacturer: "",
        media: undefined,
        departmentId: undefined,
        employeeId: undefined,
        assetGroupId: undefined,
        locationId: undefined,
        supplierId: undefined,
      };
      return assetViewModel;
    }
  }
}
